import type { StudentDashboardData } from '@services/student/studentQueries';

export type StudentPortalVersion = 'CLASSIC' | 'JOURNEY';

const CACHE_KEY = 'spPortalVersion';

/**
 * Decide which Student Portal experience to render.
 *
 * Priority:
 *  1. `?portal=journey` / `?portal=classic` in the URL — DEV builds only, for review.
 *  2. `cohort.student_portal_version` from the dashboard payload (source of truth).
 *  3. Last value seen for this browser (localStorage) — avoids a CLASSIC→JOURNEY
 *     flash on a returning Cohort-2 student's first paint before the query resolves.
 *  4. Default `CLASSIC` — so every current (Cohort 1) user is completely unaffected.
 */
export function resolvePortalVersion(
  dashboard: StudentDashboardData | undefined,
  search?: string,
): StudentPortalVersion {
  if (import.meta.env.DEV && search) {
    const override = new URLSearchParams(search).get('portal')?.toUpperCase();
    if (override === 'JOURNEY' || override === 'CLASSIC') return override;
  }

  const fromData = dashboard?.cohort?.student_portal_version;
  if (fromData === 'JOURNEY' || fromData === 'CLASSIC') {
    try { localStorage.setItem(CACHE_KEY, fromData); } catch { /* ignore */ }
    return fromData;
  }

  try {
    return localStorage.getItem(CACHE_KEY) === 'JOURNEY' ? 'JOURNEY' : 'CLASSIC';
  } catch {
    return 'CLASSIC';
  }
}
